/**
 * Notification Type to Route Mapping
 *
 * Maps notification types to their target page routes.
 * Used by PageContainer to navigate when a notification is clicked.
 *
 * When a notification is clicked:
 * 1. Look up the route from this mapping
 * 2. Build URL with query params: /route?highlight={itemId}
 * 3. Navigate using React Router
 * 4. Target page reads the query param and handles the jump/highlight
 */

import {
  NOTIFICATION_TYPE_REGISTRY,
  isPersonalNotificationType,
  type NotificationType,
} from './notificationRegistry';

interface NotificationRouteConfig {
  /** The base route path (e.g., '/rentals') */
  route: string;
  /** Query param name for the item ID (defaults to 'highlight') */
  itemParam?: string;
  /** Additional query params to add based on notification type */
  additionalParams?: Record<string, string>;
}

/**
 * Get route configuration for a notification type
 * @param type - The notification type
 * @returns Route config or null if not found
 */
function getNotificationRoute(type: string): NotificationRouteConfig | null {
  const entry = NOTIFICATION_TYPE_REGISTRY[type as NotificationType];
  if (!entry) return null;
  const route = 'route' in entry ? (entry.route as string | undefined) : undefined;
  if (!route) return null; // personal types route by the notification's own url
  const itemParam = 'itemParam' in entry ? entry.itemParam : undefined;
  const additionalParams = 'additionalParams' in entry ? { ...entry.additionalParams } : undefined;
  return {
    route,
    itemParam,
    additionalParams,
  };
}

/**
 * Build the full URL for a notification click
 * @param type - The notification type
 * @param itemId - The item ID to highlight
 * @param notificationId - The notification ID (for tracking)
 * @returns The full URL path with query params, or null if no route mapping exists
 */
export function buildNotificationUrl(
  type: string,
  itemId?: string,
  notificationId?: string
): string | null {
  const config = getNotificationRoute(type);
  if (!config) {
    return null;
  }

  const params = new URLSearchParams();

  // Add item ID param
  if (itemId) {
    const paramName = config.itemParam || 'highlight';
    params.set(paramName, itemId);
  }

  // Add notification ID for tracking/marking as read
  if (notificationId) {
    params.set('notificationId', notificationId);
  }

  // Add any additional params (e.g., modal=cross-location)
  if (config.additionalParams) {
    for (const [key, value] of Object.entries(config.additionalParams)) {
      params.set(key, value);
    }
  }

  const queryString = params.toString();
  return queryString ? `${config.route}?${queryString}` : config.route;
}

/**
 * Only same-origin in-app paths may be navigated to from a notification:
 * must start with a single '/', no protocol-relative '//' and no backslashes.
 */
export function isSafeInternalPath(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') && !url.includes('\\');
}

/** The slice of a notification / toast the URL resolver reads. */
export interface NotificationUrlSource {
  id: string;
  type: string;
  /** 'personal' for single-recipient notifications (absent = broadcast) */
  source?: 'broadcast' | 'personal';
  /** Personal notifications: in-app path to open */
  url?: string;
  metadata?: { itemId?: string; personal?: unknown; url?: unknown; [key: string]: unknown };
}

/**
 * Whether a notification or toast is personal. A toast built from an FCM push
 * carries `metadata.personal === 'true'` instead of `source`, so both are checked,
 * as is the registry (a personal type is never a broadcast).
 */
export function isPersonalNotificationSource(n: Pick<NotificationUrlSource, 'type' | 'source' | 'metadata'>): boolean {
  return n.source === 'personal' || n.metadata?.personal === 'true' || isPersonalNotificationType(n.type);
}

/**
 * Resolve where a notification click should navigate.
 * - Personal: the doc's own `url` (or `metadata.url` for FCM-built toasts), if it is a safe in-app path.
 * - Broadcast: registry route + highlight params (buildNotificationUrl).
 * Returns null when there is nowhere to go.
 */
export function resolveNotificationUrl(n: NotificationUrlSource): string | null {
  if (isPersonalNotificationSource(n)) {
    const candidate = n.url ?? n.metadata?.url;
    return isSafeInternalPath(candidate) ? candidate : null;
  }
  return buildNotificationUrl(n.type, n.metadata?.itemId, n.id);
}
